import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    useLocationsByLoggedInUserRole,
    useDispatchSuccessNotification,
    useDispatchErrorNotification,
} from '../../hooks';
import { useWorkingUnits } from '../../hooks/api/workingUnits';
import { releaseTotals } from '../../services/biddingCalculated';
import { QueryKey } from '../../enums/QueryKey';
import { queryClient } from '../../services/queryClient';
import { appTimezone } from '../../config';
import ReleaseTotalsDialog from './ReleaseTotalsDialog';
import dayjs from '@utils/dayjs';

const getSelectedData = (selectedRecords, allRecords) => {
    if (selectedRecords.find((r) => r.id === -1)) {
        return allRecords;
    }
    return selectedRecords;
};

const ReleaseBiddingTotalsDialog = ({
    open,
    onClose,
    date,
    workingUnitLocation,
    isSubsequentRelease,
    onSubsequentReleaseChange,
}) => {
    const { t } = useTranslation('bidding');
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    const [locationOptions] = useLocationsByLoggedInUserRole({ prependAllLocationsOption: true });
    const [selectedLocations, setSelectedLocations] = useState([]);

    const [workingUnitOptions] = useWorkingUnits({ hasMatchingOfferedCluster: true, prependAllOption: true });
    const [selectedWorkingUnits, setSelectedWorkingUnits] = useState([]);

    const { mutate: handleReleaseTotals, isLoading } = useMutation({
        mutationFn: releaseTotals,
        onSuccess: (response) => {
            queryClient.invalidateQueries([QueryKey.LastBiddingCalculatedTotalsReleaseDate, { workingUnitLocation }]);

            const { success, message } = response;
            if (success) {
                dispatchSuccessNotification({ message: message || t('notification.totalsReleased') });
                queryClient.invalidateQueries([QueryKey.CorrectedTotals, { date, workingUnitLocation }]);
                onClose();
            } else {
                dispatchErrorNotification({ message: message || t('notification.noTotalsFound') });
            }
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message || t('notification.totalsReleaseFailed') });
        },
    });

    return (
        <ReleaseTotalsDialog
            open={open}
            onClose={onClose}
            date={date}
            onRelease={() =>
                handleReleaseTotals({
                    dateFrom: dayjs(date).tz(appTimezone).startOf('month').toISOString(),
                    dateTo: dayjs(date).tz(appTimezone).endOf('month').toISOString(),
                    locations: getSelectedData(selectedLocations, locationOptions),
                    workingUnits: getSelectedData(selectedWorkingUnits, workingUnitOptions),
                    isSubsequentRelease,
                })
            }
            selectedLocations={selectedLocations}
            onSelectedLocationsChange={setSelectedLocations}
            workingUnitOptions={workingUnitOptions}
            selectedWorkingUnits={selectedWorkingUnits}
            onSelectedWorkingUnitsChange={setSelectedWorkingUnits}
            isLocationMultiSelect={true}
            releaseButtonText={t('action.release')}
            isLoading={isLoading}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        onChange={() => onSubsequentReleaseChange(!isSubsequentRelease)}
                        checked={isSubsequentRelease}
                    ></Checkbox>
                }
                label={t('field.subsequentRelease')}
            />
        </ReleaseTotalsDialog>
    );
};

export default ReleaseBiddingTotalsDialog;
